/**
 * Cliente REST del CATÁLOGO público (guest-first). El catálogo de cursos
 * públicos es autoritativo del SERVIDOR (no sync): explorar y abrir un curso
 * NO requiere cuenta. El progreso, en cambio, es privado y va por PowerSync.
 *
 * FUENTE ÚNICA DE TIPOS (plan 06, E0): los DTOs se IMPORTAN del OpenAPI
 * generado desde los `struct` de `models/course.rs`. Este fichero NO declara
 * ningún tipo propio — una interfaz escrita a mano que imite un struct de Rust
 * es una segunda fuente de verdad que diverge en la primera corrección
 * (`docs/DATA_ACCESS_POLICY.md` §3.3). Si un DTO falta en el generado, la causa
 * es que su handler no está en `paths(...)` de `apps/server/src/main.rs`.
 */
import type { components } from "@space/client";

export type CatalogEntryDTO = components["schemas"]["CatalogEntryDTO"];
export type CourseTreeDTO = components["schemas"]["CourseTreeDTO"];

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, "")}${path}`;
}

/**
 * Parses a server JSON response into its typed DTO. `Response.json()` is `any`,
 * so this is the single validated trust-boundary parse for the catalog
 * endpoints (our own server contract).
 */
async function readJson<T>(res: Response): Promise<T> {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed catalog DTO
  return (await res.json()) as T;
}

/** Lista el catálogo público (opcionalmente filtrado por texto). */
export async function fetchCatalog(
  baseUrl: string,
  opts: { q?: string; signal?: AbortSignal } = {},
): Promise<CatalogEntryDTO[]> {
  const url = new URL(joinUrl(baseUrl, "/api/catalog"));
  if (opts.q && opts.q.trim() !== "") {
    url.searchParams.set("q", opts.q.trim());
  }
  const res = await fetch(url.toString(), { signal: opts.signal });
  if (!res.ok) {
    throw new Error(`catalog fetch failed: ${res.status}`);
  }
  return readJson<CatalogEntryDTO[]>(res);
}

/** Árbol completo de un curso público (para consumir offline tras descargar). */
export async function fetchCatalogCourse(
  baseUrl: string,
  courseId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CourseTreeDTO> {
  const res = await fetch(joinUrl(baseUrl, `/api/catalog/course/${encodeURIComponent(courseId)}`), {
    signal: opts.signal,
  });
  if (!res.ok) {
    throw new Error(`course fetch failed: ${res.status}`);
  }
  return readJson<CourseTreeDTO>(res);
}
